import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CcfTranslatorProviderType,
  PopOverMenuItem,
} from '@nice-ccf/ui-controls';
import { Direction } from '@mui/material';
import { Navigation } from '../enums/navigation-menus';
import { localeTranslationMap, translations } from '@nice-ccf/i18n';
import { NavigationItem } from './ccf-navigation/useNavigationItems';
import { StorageKeys, PersistentPanel } from '@nice-ccf/core-sdk';

export type SupportedLanguages = 'en' | '';

export interface ConversationMessage {
  name?: string;
  text?: string;
  status?: string;
  timestamp?: string;
  color?: string;
}

export const GLOBAL_APP_FEATURE_KEY = 'global';

export interface AppState {
  version: string;
  direction: Direction;
  language: {
    name: string;
    translations: CcfTranslatorProviderType;
  };
  selectedMenuName: Navigation;
  simulatorTabVisibilityState: boolean;
  partnerAccessTabVisibilityState: boolean;
  sentiment: any;
  cardName: any;
  isEditInlineNote: boolean;
  isEditContactCardNote: boolean;
  acdLeftNavExpanded: boolean;
  locale: string;
  isPageReloaded: boolean;
  commitmentPopoverItems: PopOverMenuItem[];
  reArrangeMenu: boolean;
  panelAppNavigationItems: NavigationItem[];
  quickAppNavigationItems: NavigationItem[];
  updatedContactHistoryList: any;
  persistentPanels: PersistentPanel[];
  activePersistentPanel: PersistentPanel;
  appSpaceRatio: any;
  showPartnerAccessPage: boolean;
  renderVoicePrefPage: boolean;
  authenticationStage?: number;
  showAgentMessagesNotification: boolean;
  conversationMessage: ConversationMessage | null;
  enableLyraDesign: boolean;
}
const initialCommitmentPopoverMenuItems: PopOverMenuItem[] = [
  {
    translationKey: 'commitment',
    type: 'commitment',
    closeOnSelection: true,
  },
  {
    translationKey: 'activity',
    type: 'activity',
    closeOnSelection: true,
  },
  {
    translationKey: 'other',
    type: 'other',
    closeOnSelection: true,
  },
];

const navigationItemsLS = JSON.parse(
  localStorage.getItem(StorageKeys.CXONE_NAVIGATION_ITEMS) || '{}'
);
const navigationItemsLSLength: number = Object.keys(navigationItemsLS).length;

const initialQuickAppsMenuItems: NavigationItem[] =
  navigationItemsLSLength &&
  navigationItemsLS.quickApps &&
  navigationItemsLS.quickApps.length
    ? navigationItemsLS.quickApps
    : [];

const initialPanelAppsMenuItems: NavigationItem[] =
  navigationItemsLSLength &&
  navigationItemsLS.panelApps &&
  navigationItemsLS.panelApps.length
    ? navigationItemsLS.panelApps
    : [];

export const initialGlobalState: AppState = {
  version: '0.0.1',
  direction: translations.direction as Direction,
  language: {
    name: 'en-US',
    translations: translations,
  },
  selectedMenuName: Navigation.CONTACTHISTORY,
  simulatorTabVisibilityState: true,
  partnerAccessTabVisibilityState: true,
  sentiment: { show: false, sentiment: '', cardName: '' },
  cardName: '',
  isEditInlineNote: true,
  isEditContactCardNote: false,
  acdLeftNavExpanded: true,
  locale: 'en',
  isPageReloaded: false,
  commitmentPopoverItems: initialCommitmentPopoverMenuItems,
  reArrangeMenu: false,
  panelAppNavigationItems: initialPanelAppsMenuItems,
  quickAppNavigationItems: initialQuickAppsMenuItems,
  updatedContactHistoryList: [],
  persistentPanels: [
    {
      persistentPanelId: 1,
      persistentPanelLabel: 'CXone Expert',
      persistentPanelURI:
        'https://expert-demo-clone001.mindtouch.us/@embed/d50d592d3c9aa6ac86d732c8bef56b811247d4baffd87610ff1b7e5263914155.html',
    },
    {
      persistentPanelId: 2,
      persistentPanelLabel: 'PM SSO',
      persistentPanelURI: 'https://demo-cxonepm.clearviewportal.com/',
    },
    {
      persistentPanelId: 3,
      persistentPanelLabel: 'Surfly',
      persistentPanelURI: 'https://surfly-cxone-953a75b368ee.herokuapp.com/',
    },
  ] as PersistentPanel[],
  activePersistentPanel: {} as PersistentPanel,
  appSpaceRatio: 50,
  showPartnerAccessPage: false,
  renderVoicePrefPage: true,
  authenticationStage: 1,
  showAgentMessagesNotification: false,
  conversationMessage: null,
  enableLyraDesign: true,
};

export const globalSlice = createSlice({
  name: GLOBAL_APP_FEATURE_KEY,
  initialState: initialGlobalState,
  reducers: {
    /**
     * Function to set app space aspect ratio
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setAppSpaceRatio(0));
     * @returns updated state
     */
    setAppSpaceRatio: (state, action) => {
      return {
        ...state,
        appSpaceRatio: action.payload,
      };
    },
    setLanguageConfiguration: (state, action) => {
      const languageTranslations: CcfTranslatorProviderType =
        localeTranslationMap.get(action.payload) || translations;
      document.body.dir = languageTranslations.direction;
      return {
        ...state,
        direction: languageTranslations.direction as Direction,
        language: {
          name: languageTranslations.locale,
          translations: languageTranslations,
        },
      };
    },
    setExpandedAcdLeftNav: (state, action) => {
      return {
        ...state,
        acdLeftNavExpanded: action.payload,
      };
    },
    reArrangeMenu: (state, action) => {
      const srcIndex =
        action.payload.src.droppableId === 'droppable-3'
          ? action.payload.src.index
          : action.payload.src.index;
      const destIndex =
        action.payload.dest.droppableId === 'droppable-3'
          ? action.payload.dest.index
          : action.payload.dest.index;

      const isAppSpaceMenu = action.payload.isAppSpaceMenu;
      let copyItems = !isAppSpaceMenu
        ? [...state.quickAppNavigationItems]
        : [...state.panelAppNavigationItems];
      const contactSpecificApps: NavigationItem[] = [];
      const globalApps: NavigationItem[] = [];
      if (
        action.payload.dest.droppableId === 'droppable-2' ||
        action.payload.dest.droppableId === 'droppable-3'
      ) {
        copyItems.forEach((item) => {
          const menuItem = action.payload.navigationItemList.find(
            (navItem: NavigationItem) => navItem.menuName === item.menuName
          );
          if (menuItem) {
            if (item.contactApp) {
              contactSpecificApps.push(item);
            } else {
              globalApps.push(item);
            }
          }
        });
        copyItems = [...globalApps];
      }

      //Rearrange the items on drop of element
      copyItems.splice(destIndex, 0, copyItems.splice(srcIndex, 1)[0]);
      copyItems = [...contactSpecificApps, ...copyItems];
      const CXoneNavigationItems = {
        quickApps: isAppSpaceMenu
          ? action.payload.navigationItemsLS.quickApps
          : copyItems,
        panelApps: isAppSpaceMenu
          ? copyItems
          : action.payload.navigationItemsLS.panelApps,
      };
      localStorage.setItem(
        StorageKeys.CXONE_NAVIGATION_ITEMS,
        JSON.stringify(CXoneNavigationItems)
      );
      return {
        ...state,
        reArrangeMenu: true,
        quickAppNavigationItems: CXoneNavigationItems.quickApps,
        panelAppNavigationItems: CXoneNavigationItems.panelApps,
      };
    },
    setReArrangeMenu: (state, action) => {
      return {
        ...state,
        reArrangeMenu: action.payload,
      };
    },
    initializeCXoneNavigationItems: (state, action) => {
      return {
        ...state,
        quickAppNavigationItems: action.payload.quickApps,
        panelAppNavigationItems: action.payload.panelApps,
      };
    },
    /**
     * Function to set selected menu
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setSelectedMenu(`{ name: menuName }`));
     * @returns
     */
    setSelectedMenu(state, action) {
      return {
        ...state,
        selectedMenuName: action.payload.name,
      };
    },
    /**
     * Function to set selected menu
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setSelectedMenu(`{ name: menuName }`));
     * @returns
     */
    setUpdatedContactHistory(state, action) {
      return {
        ...state,
        updatedContactHistoryList: action.payload,
      };
    },
    /**
     * Function to set page reload flag
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setPageReload(`{ isPageReloaded: true/false }`));
     * @returns updated state
     */
    setPageReload(state, action) {
      return {
        ...state,
        isPageReloaded: action.payload,
      };
    },
    /**
     * Function to set Edit Inline Note
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setIsEditInlineNote());
     * @returns
     */
    setIsEditInlineNote(state, action) {
      return {
        ...state,
        isEditInlineNote: action.payload,
      };
    },
    /**
     * Function to set Edit Inline Note
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setIsEditInlineNote());
     * @returns
     */
    renderVoicePrefPage(state, action) {
      return {
        ...state,
        renderVoicePrefPage: action.payload,
      };
    },

    /**
     * Function to set Edit Inline Note
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setIsEditInlineNote());
     * @returns
     */
    setAuthenticationStage(state, action) {
      return {
        ...state,
        authenticationStage: action.payload,
      };
    },

    /**
     * Function to set Edit Note
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setIsEditContactCardNote());
     * @returns
     */
    setIsEditContactCardNote(state, action) {
      return {
        ...state,
        isEditContactCardNote: action.payload,
      };
    },

    /**
     * Function to set setSimulatorTabVisibilityState
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setSimulatorTabVisibilityState());
     * @returns
     */
    setSimulatorTabVisibilityState(state, action) {
      return {
        ...state,
        simulatorTabVisibilityState: action.payload,
      };
    },

    /**
     * Function to set setPartnerAccessTabVisibilityState
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setPartnerAccessTabVisibilityState());
     * @returns
     */
    setPartnerAccessTabVisibilityState(state, action) {
      return {
        ...state,
        partnerAccessTabVisibilityState: action.payload,
      };
    },

    /**
     * Function to set selected menu
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setSelectedMenu(`{ name: menuName }`));
     * @returns
     */
    setContactSentiment(state, action) {
      return {
        ...state,
        sentiment: action.payload,
      };
    },
    /**
     * Function to select Custom Workspaces
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setActivePersistentPanel([]));
     * @returns updated state
     */
    setActivePersistentPanel(
      state: AppState,
      action: PayloadAction<PersistentPanel>
    ) {
      const persistentPanelDetails: PersistentPanel = action.payload;
      return {
        ...state,
        activePersistentPanel: persistentPanelDetails,
      };
    },
    /**
     * Function to set Custom Workspaces
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setPersistentPanels([]));
     * @returns updated state
     */
    setPersistentPanels(
      state: AppState,
      action: PayloadAction<PersistentPanel[]>
    ) {
      const persistentPanelDetails: PersistentPanel[] = action.payload;
      return {
        ...state,
        persistentPanels: persistentPanelDetails,
      };
    },
    /**
     * Reducer function to store current selected setLocale
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setLocale(locale));
     * @returns - updated state
     */
    setLocale(state, action) {
      state.locale = action.payload;
      return state;
    },

    /**
     * Function to set RTIG Suggestion card details menu
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setRtigSuggestionCardDetails());
     * @returns
     */
    setRtigSuggestionCardDetails(state, action) {
      return {
        ...state,
        cardName: action.payload,
      };
    },
    /**
     * Function to set RTIG Suggestion card details menu
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setRtigSuggestionCardDetails());
     * @returns
     */
    setShowPartnerAccessPage(state, action) {
      return {
        ...state,
        showPartnerAccessPage: action.payload,
      };
    },
    /**
     * Function to set Agent Message Notification
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setShowAgentMessagesNotification());
     * @returns
     */
    setShowAgentMessagesNotification(state, action) {
      return {
        ...state,
        showAgentMessagesNotification: action.payload,
      };
    },

    /**
     * @example - dispatch(setConversationMessage(message));
     */
    setConversationMessage(
      state,
      action: PayloadAction<ConversationMessage | null>
    ) {
      return {
        ...state,
        conversationMessage: action.payload,
      };
    },

    /**
     * Function to toggle Lyra design on/off
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(setEnableLyraDesign(true));
     * @returns updated state
     */
    setEnableLyraDesign(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        enableLyraDesign: action.payload,
      };
    },
  },
});

export const globalReducer = globalSlice.reducer;

export const globalActions = globalSlice.actions;

/**
 * Function to get app state
 * @param rootState - AppState
 * @returns It returns app state
 * @example - const appState = getAppState(rootState)
 */
export const getAppState = (rootState: { global: AppState }) =>
  rootState[GLOBAL_APP_FEATURE_KEY];

export const getApplicationLanguageTranslations = createSelector(
  getAppState,
  (state: AppState) => state.language.translations
);

export const getApplicationDirection = createSelector(
  getAppState,
  (state: AppState) => state.direction
);

export const getContactSentiment = createSelector(
  getAppState,
  (state: AppState) => state.sentiment
);
export const getCommitmentItems = createSelector(
  getAppState,
  (state: AppState) => state?.commitmentPopoverItems
);
export const getRtigSuggestionCardName = createSelector(
  getAppState,
  (state: AppState) => state.cardName
);

export const getIsEditInlineNote = createSelector(
  getAppState,
  (state: AppState) => state.isEditInlineNote
);

export const getIsEditContactCardNote = createSelector(
  getAppState,
  (state: AppState) => state.isEditContactCardNote
);

export const getSelectedMenuName = createSelector(
  getAppState,
  (state) => state.selectedMenuName
);

export const selectAcdLeftNavExpanded = createSelector(
  getAppState,
  (state: AppState) => {
    return state.acdLeftNavExpanded;
  }
);

export const getApplicationLocale = createSelector(
  getAppState,
  (state: AppState) => state.locale
);

export const getAppSpaceRatio = createSelector(
  getAppState,
  (state: AppState) => state?.appSpaceRatio
);

export const getPanelAppNavigationItems = createSelector(
  getAppState,
  (state: AppState) => state?.panelAppNavigationItems
);

export const getQuickAppNavigationItems = createSelector(
  getAppState,
  (state: AppState) => state?.quickAppNavigationItems
);

export const getSimulatorTabVisibilityState = createSelector(
  getAppState,
  (state: AppState) => state?.simulatorTabVisibilityState
);

export const getPartnerAccessTabVisibilityState = createSelector(
  getAppState,
  (state: AppState) => state?.partnerAccessTabVisibilityState
);

export const getUpdatedContactHistoryList = createSelector(
  getAppState,
  (state: AppState) => state?.updatedContactHistoryList
);

export const getShowPartnerAccessPage = createSelector(
  getAppState,
  (state: AppState) => state?.showPartnerAccessPage
);

export const getCustomWorkspaces = createSelector(
  getAppState,
  (state: AppState) => state.persistentPanels
);

export const getActivePersistentPanel = createSelector(
  getAppState,
  (state: AppState) => state.activePersistentPanel
);

export const isPageReloaded = createSelector(
  getAppState,
  (state: AppState) => state.isPageReloaded
);

export const getRenderingVoicePrefPage = createSelector(
  getAppState,
  (state: AppState) => state.renderVoicePrefPage
);

export const getAuthenticationStage = createSelector(
  getAppState,
  (state: AppState) => state.authenticationStage
);

export const getReArrangeMenuState = createSelector(
  getAppState,
  (state: AppState) => state.reArrangeMenu
);

export const getShowAgentMessagesNotification = createSelector(
  getAppState,
  (state: AppState) => state.showAgentMessagesNotification
);

export const getConversationMessage = createSelector(
  getAppState,
  (state: AppState) => state.conversationMessage
);

export const getEnableLyraDesign = createSelector(
  getAppState,
  (state: AppState) => state.enableLyraDesign
);
